import React from 'react'
import { InferType } from 'yup'
import { useMutation, useQuery } from '@tanstack/react-query'
import { captureException } from '@sentry/react'
import { Modal } from '../../../common/Modal'
import { ModalHeader } from '../../../common/Modal/ModalHeader'
import { ModalBody } from '../../../common/Modal/ModalBody'
import { useAppTranslation } from '../../../../hooks/useAppTranslation'
import { ToastType } from '../../../common/Toast/toast-type'
import { useToastNotifications } from '../../../../hooks/useToastNotification'
import { useCurrentSociety } from '../../../../hooks/useCurrentSociety'
import { CreateDocumentForm } from '../CreateUpdateDocumentForm/CreateDocumentForm'
import { UpdateDocumentForm } from '../CreateUpdateDocumentForm/UpdateDocumentForm'
import { societyDocumentUpdateSchema } from '../../../../forms/schemas/society_document_update'
import { societyDocumentUploadSchema } from '../../../../forms/schemas/society_document_upload'
import { IDocumentModel } from '../../../../interfaces/models/documents.interfaces'
import {
  createDocument,
  documentQueries,
  patchDocument,
} from '../../../../api/documents'
import { NDocuments } from '../../../../interfaces/services/documents.interfaces'

interface CreateUpdateDocumentModalProps {
  show: boolean
  close: () => void
  section: IDocumentModel['section']
  parentId: string
  children?: React.ReactNode
  id?: string
  isBoardDocuments?: boolean
}

export const CreateUpdateDocumentModal = ({
  id,
  parentId,
  section,
  children,
  show,
  close,
  isBoardDocuments = false,
}: CreateUpdateDocumentModalProps): JSX.Element => {
  const { translate } = useAppTranslation()
  const { society } = useCurrentSociety()
  const { data: document } = useQuery(documentQueries.getDocument(id))
  const { setToastNotification } = useToastNotifications()

  const updateMode = !!id
  const showErrorMessage = (): void => {
    const message = updateMode
      ? 'createUpdateDocument.flashMessage.updateDocumentFailure'
      : 'createUpdateDocument.flashMessage.createDocumentFailure'
    setToastNotification(ToastType.DANGER, translate(message))
  }

  const showSuccessMessage = (): void => {
    const message = updateMode
      ? 'createUpdateDocument.flashMessage.updateDocumentSuccess'
      : 'createUpdateDocument.flashMessage.createDocumentSuccess'
    setToastNotification(ToastType.SUCCESS, translate(message))
  }
  const { mutate: mutatePatchDocument, isPending: isPatchingDocument } =
    useMutation({
      mutationFn: patchDocument,
      onSuccess: () => {
        close()
        showSuccessMessage()
      },
      onError: () => {
        showErrorMessage()
      },
    })

  const getSuccessMessage = (isSingular: boolean): string => {
    return `${translate(
      isSingular
        ? 'createUpdateDocument.flashMessage.createDocumentSingularSuccess'
        : 'createUpdateDocument.flashMessage.createDocumentPluralSuccess'
    )}`
  }

  const getFailureMessage = (isSingular: boolean): string => {
    return `${translate(
      isSingular
        ? 'createUpdateDocument.flashMessage.createDocumentSingularFailure'
        : 'createUpdateDocument.flashMessage.createDocumentPluralFailure'
    )}`
  }

  const showMessage = (type: ToastType, message: string): void => {
    setToastNotification(type, message)
  }

  const { mutate: mutateCreateDocuments, isPending: isCreatingDocuments } =
    useMutation({
      mutationFn: async (params: NDocuments.NCreate.IRequestBody[]) => {
        const promises = params.map(async (body) => {
          return (await createDocument(body)).data.data
        })

        return Promise.all(promises)
      },
      onSuccess: (response) => {
        const isOneFile = response.length === 1
        showMessage(ToastType.SUCCESS, getSuccessMessage(isOneFile))
        close()
      },
      onError: (_error, params) => {
        const isOneFile = params.length === 1
        showMessage(ToastType.DANGER, getFailureMessage(isOneFile))
      },
    })

  const onSubmitUpdateDocument = (
    data: InferType<typeof societyDocumentUpdateSchema>
  ): void => {
    id && mutatePatchDocument({ id, body: data })
  }

  const onSubmitCreateDocument = async (
    data: InferType<typeof societyDocumentUploadSchema>
  ): Promise<void> => {
    if (!data.files) {
      showMessage(
        ToastType.DANGER,
        translate('flashMessage.somethingWentWrongError')
      )
      return
    }

    const files = data.files.map((_file) => {
      const doc: NDocuments.NCreate.IRequestBody = {
        data: _file.base64,
        name: _file.name,
        filename: _file.filename,
        parentId,
        societyId: society?._id,
        notificationSettings: data.notificationSettings,
        section,
        type: _file.filename.split('.').pop() as IDocumentModel['type'],
      }
      return doc
    })
    mutateCreateDocuments(files as NDocuments.NCreate.IRequestBody[])
  }

  const onError = (error: unknown): void => {
    captureException(error)
    showErrorMessage()
  }

  const loading = isPatchingDocument || isCreatingDocuments

  return (
    <>
      {children}
      <Modal show={show}>
        {{
          header: (
            <ModalHeader onClose={close}>
              {translate(
                updateMode
                  ? 'createUpdateDocument.editDocumentTitle'
                  : 'createUpdateDocument.addNewDocumentTitle'
              )}
            </ModalHeader>
          ),
          body: (
            <ModalBody>
              {updateMode && document ? (
                <UpdateDocumentForm
                  onError={onError}
                  onSubmit={onSubmitUpdateDocument}
                  onClose={close}
                  loading={loading}
                  document={document}
                />
              ) : (
                <CreateDocumentForm
                  onError={onError}
                  onSubmit={onSubmitCreateDocument}
                  onClose={close}
                  loading={loading}
                  isBoardDocuments={isBoardDocuments}
                />
              )}
            </ModalBody>
          ),
        }}
      </Modal>
    </>
  )
}
